import React from "react";
import PageLayout from "../../../components/Layout/PageLayout";

const MSA: React.FC = () => {
  const termsContent = [
    {
      title: "Master Services Agreement (MSA)",
      content: (
        <>
          <p className="text-left text-gray-600">
            This Master Services Agreement ("Agreement") is made effective as of{" "}
            {new Date().toLocaleDateString()} ("Effective Date") by and between:
          </p>
          <div className="grid md:grid-cols-2 gap-8 mb-5">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg mb-2">Provider:</h2>
              <p>25th & Staffing</p>
              <p>
                Architect Place, 2 Idowu Taylor Street, Victoria Island, Lagos
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg mb-2">Customer:</h2>
              <p>{"[Client Name]"}</p>
              <p>{"[Client Legal Entity Name]"}</p>
            </div>
          </div>

          <p className="text-left mb-5">
            Provider and Customer may be referred to individually as a "Party"
            and collectively as the "Parties."
          </p>

          {/* Services Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">1. Services</h2>
            <div className="space-y-3">
              <p>
                Provider agrees to provide Customer with access to its
                proprietary AI Recruiter Application ("Services") and any
                related implementation, training, or support services
                ("Professional Services") as detailed in one or more executed
                Order Forms or Scopes of Work (SOWs).
              </p>
              <p>
                All Order Forms and SOWs shall be governed by the terms of this
                Agreement.
              </p>
            </div>
          </section>

          {/* Fees and Payment Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">2. Fees and Payment</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">2.1. Fees:</h3>
                <p>
                  Customer shall pay the fees stated in each Order Form. All
                  fees are non refundable except as expressly provided in this
                  Agreement or the SLA.
                </p>
              </div>

              <div>
                <h3 className="mb-2">2.2. Invoicing:</h3>
                <p className="mb-2">
                  Fees will be invoiced in Nigerian Naira or agreed currency as
                  outlined in the Order Form. Unless otherwise specified in the
                  MOU:
                </p>
                <p>
                  Subscription fees are invoiced monthly or annually in advance.
                  One time setup or integration fees are invoiced upon execution
                  of the Order Form. Any milestone based fees must follow the
                  schedule defined in the MOU.
                </p>
              </div>

              <div>
                <h3 className="mb-2">2.3. Payment:</h3>
                <p>
                  Payment timelines follow the project specific schedules stated
                  in the MOU for integration, customization, subscription, or
                  milestone based work.
                </p>
              </div>
            </div>
          </section>

          {/* Term and Termination Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">3. Term and Termination</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">3.1. Term:</h3>
                <p>
                  This Agreement begins on the Effective Date and continues
                  until terminated.
                </p>
              </div>

              <div>
                <h3 className="mb-2">3.2. Subscription Term and Renewals:</h3>
                <p className="mb-2">
                  The initial subscription term will be stated in the Order
                  Form. After the Initial Term, the subscription will
                  automatically renew for successive one year or one month
                  periods based on the Customer's selected plan.
                </p>
                <p>
                  Access to the platform will be suspended if subscription fees
                  are not paid when due.
                </p>
              </div>

              <div>
                <h3 className="mb-2">3.3. Termination for Cause:</h3>
                <p className="mb-2">
                  Either Party may terminate this Agreement if the other Party
                  materially breaches any of its obligations and does not cure
                  the breach within thirty days of receiving written notice.
                  Non-payment of subscription fees or integration balances is
                  not subject to a cure period.
                </p>
                <p>
                  The Provider may immediately suspend or discontinue access and
                  project delivery until all outstanding amounts are settled.
                </p>
              </div>
            </div>
          </section>

          {/* Intellectual Property Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">4. Intellectual Property</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">4.1. Provider IP:</h3>
                <p>
                  Provider retains all right, title, and interest in and to the
                  Services, its software, AI models, and all related
                  documentation.
                </p>
              </div>

              <div>
                <h3 className="mb-2">4.2. Customer Data:</h3>
                <p>
                  Customer retains all right, title, and interest in and to all
                  data, information, and content (including resumes and
                  candidate profiles) provided by Customer to the Services
                  ("Customer Data").
                </p>
                <p className="mt-2">
                  Customer grants Provider a non-exclusive, worldwide,
                  royalty-free license to use, copy, and process Customer Data
                  solely for the purpose of providing the Services.
                </p>
              </div>

              <div>
                <h3 className="mb-2">4.3. Feedback:</h3>
                <p>
                  Customer may provide feedback to Provider regarding the
                  Services. Provider is free to use, implement, and modify such
                  feedback without any obligation to Customer.
                </p>
              </div>
            </div>
          </section>

          {/* Confidentiality Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">5. Confidentiality</h2>
            <p>
              Each Party ("Receiving Party") agrees to hold in confidence all
              non-public information disclosed by the other Party ("Disclosing
              Party"). The Receiving Party shall use such Confidential
              Information only for the purpose of performing its obligations
              under this Agreement and shall not disclose it to any third party
              without the Disclosing Party's prior written consent.
            </p>
          </section>

          {/* Data and Security Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">6. Data and Security</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">6.1. Security:</h3>
                <p>
                  Provider shall implement and maintain appropriate technical
                  and organizational security measures to protect Customer Data,
                  as detailed in the Service Level Agreement (Exhibit B).
                </p>
              </div>

              <div>
                <h3 className="mb-2">6.2. Data Privacy:</h3>
                <p>
                  All data processing shall be governed by the Data Processing
                  Addendum (Exhibit C), which is hereby incorporated by
                  reference.
                </p>
              </div>
            </div>
          </section>

          {/* Warranties and Disclaimers Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">7. Warranties and Disclaimers</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">7.1. Warranties:</h3>
                <p>
                  Provider warrants that the Services will perform materially in
                  accordance with the applicable documentation and the Service
                  Level Agreement (Exhibit B).
                </p>
              </div>

              <div>
                <h3 className="mb-2">7.2. Disclaimer:</h3>
                <p className="font-medium">
                  EXCEPT AS EXPRESSLY PROVIDED HEREIN, PROVIDER MAKES NO
                  WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED (INCLUDING
                  ANY WARRANTY OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR
                  PURPOSE).
                </p>
              </div>
            </div>
          </section>

          {/* Limitation of Liability Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">8. Limitation of Liability</h2>
            <div className="space-y-3">
              <p className="font-medium">
                IN NO EVENT SHALL EITHER PARTY'S AGGREGATE LIABILITY ARISING OUT
                OF THIS AGREEMENT EXCEED THE TOTAL FEES PAID OR PAYABLE BY
                CUSTOMER TO PROVIDER DURING THE TWELVE (12) MONTHS PRECEDING THE
                CLAIM.
              </p>
              <p className="font-medium">
                IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY LOST PROFITS,
                INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.
              </p>
            </div>
          </section>

          {/* General Provisions Section */}
          <section className="mb-5">
            <h2 className="text-xl mb-4">9. General Provisions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2">9.1. Governing Law:</h3>
                <p>
                  This Agreement shall be governed by and construed in
                  accordance with the laws of the Federal Republic of Nigeria.
                </p>
              </div>

              <div>
                <h3 className="mb-2">9.2. Entire Agreement:</h3>
                <p>
                  This Agreement, including all Exhibits, Order Forms, and SOWs,
                  constitutes the entire agreement between the Parties and
                  supersedes all prior proposals and understandings.
                </p>
              </div>
            </div>
          </section>
        </>
      ),
    },
  ];

  return (
    <PageLayout>
      <div className="w-full">
        <div className="bg-white rounded-[10px] shadow-sm mb-5">
          <div className="text-center py-3">
            <h2 className="mb-2 text-[20px] md:text-[35px] font-[500] text-center">
              {termsContent?.map((item) => item?.title)}
            </h2>
          </div>
          <div className="text-gray-700 leading-relaxed">
            {termsContent?.map((item) => item?.content)}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MSA;
